import React, { useEffect, useState, useRef, useContext } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { black, mediumInter, bold, colors, rebrandColors } from '@/styles';
import { FileInput, Button, ClickableElement, LoadingDots } from '@/atoms';
import { LabelsMenu } from '@/layouts';
import { UploadArrow, AddCircle, Upload } from '@/svgs';
import { SearchBar, DropDown } from '@/molecules';
import { VideoLibraryApi, UserConfigContext } from '@/classes';

/**
 * LibraryHeader component
 *
 * @param {import('.').LibraryHeaderProps} props
 */
export function LibraryHeader({
  videoLibraryApi,
  canInsert,
  className,
  containerSize,
  defaultDropdownValue,
  dropdownItems,
  handleDropdownItemChange,
  handleFoldersChange,
  layout,
  loadingTeamFolders,
  onMetricTriggered,
  onSearch,
  onUpload,
  onUploadError,
  showInsertButton,
  showUploadButton,
  title,
  teamFolders,
  isRebrand,
}) {
  // default to the containerSize prop, but calculate our size if containerSize is
  // an empty string for clients that won't pass this in...
  const header = useRef(null);
  const [internalSize, setInternalSize] = useState(containerSize);
  const userConfig = useContext(UserConfigContext);

  // eslint-disable-next-line no-unused-vars
  const [api, setApi] = useState(videoLibraryApi || new VideoLibraryApi());

  useEffect(() => {
    if (internalSize === '' && header.current) {
      const bounding = header.current.getBoundingClientRect();
      setInternalSize(api.getSmallestBreakpointForWidth(bounding?.width) ?? '');
    }
  }, [api, internalSize, header]);

  const sizeClassName = internalSize ? `--${internalSize}` : '';
  const libraryHeaderClassName = sizeClassName ? `library-header${sizeClassName}` : '';

  const teamFolderMenuItems = () => {
    const folderMenuItems = teamFolders?.map((folder) => ({
      label: folder.name,
      value: folder.id,
    }));
    folderMenuItems.unshift({
      label: 'All folders',
      value: '',
    });
    return folderMenuItems;
  };

  if (userConfig.state === 'loading') {
    return <LoadingDots />;
  }

  const uploadEnabled =
    userConfig.state === 'ready' ? Boolean(userConfig.config.UPLOAD_ENABLED) : true;

  return (
    <div
      ref={header}
      className={`${className} layout--${layout}${sizeClassName ? ` size${sizeClassName}` : ''} ${
        isRebrand ? 'brand--new' : ''
      }`}
    >
      <div className={`library-header ${libraryHeaderClassName}`}>
        <div className="flex-container">
          {/* only show the title above xsmall breakpoint */}
          {layout === 'default' && !['xsmall', 'mobile'].includes(internalSize) && (
            <p className="title">{title}</p>
          )}
          {layout === 'compact' && (
            <LabelsMenu
              // this key is necessary (and necessary to be dynamic) to avoid an
              // issue with DropDown and default values. The compact layout will
              // render the DropDown herein before we have a valid default value
              // (see VideoLibrary.getDropDownLabelFromValue()), but once the
              // DropDown is rendered with one default value, it won't accept
              // a new default value later when it is valid...
              // TL;DR: fixes a bug where reloading/re-rendering would have a
              // blank value
              key={`labels-${defaultDropdownValue}`}
              canInsert={canInsert}
              showInsertButton={showInsertButton}
              className="library-header--labels"
              containerSize={containerSize}
              layout={layout}
              defaultDropdownValue={defaultDropdownValue}
              dropdownItems={dropdownItems}
              handleDropdownItemChange={handleDropdownItemChange}
              handleFoldersChange={handleFoldersChange}
              loadingTeamFolders={loadingTeamFolders}
              disableButtons={false}
              onMenuOpen={(opened) => {
                if (opened) {
                  onMetricTriggered('click.[COMPONENT].labels');
                }
              }}
              teamFolders={teamFolders}
              isRebrand={isRebrand}
            />
          )}

          <div className="searchbar-container">
            <SearchBar
              isRebrand={isRebrand}
              placeHolder={isRebrand ? 'Search Videos' : 'Search'}
              onSelect={onSearch}
              size={isRebrand ? 'medium' : 'small'}
              onFocus={() => {
                onMetricTriggered('[COMPONENT].search.focus');
              }}
            />
          </div>

          {layout === 'default' && uploadEnabled && (
            <FileInput
              onClick={() => onMetricTriggered('click.[COMPONENT].upload')}
              onFileChange={onUpload}
              multiple
              onError={onUploadError}
              className="button--upload"
            >
              {isRebrand ? (
                <Button
                  isRebrand
                  rectangle
                  textColor={rebrandColors.electricBlue}
                  hoverColor={rebrandColors.electricBlue}
                  size="medium"
                  color={rebrandColors.skyBlue}
                  label="Upload"
                  icon={<UploadArrow color={rebrandColors.electricBlue} size="4.7" />}
                />
              ) : (
                <Button
                  size="small"
                  color={colors.kermit}
                  label="Upload"
                  icon={<AddCircle fill={colors.white} size={4.7} />}
                />
              )}
            </FileInput>
          )}
          {layout === 'compact' && uploadEnabled && showUploadButton && (
            <FileInput
              onClick={() => onMetricTriggered('click.[COMPONENT].upload')}
              onFileChange={onUpload}
              multiple
              onError={onUploadError}
              className="button--upload"
            >
              <ClickableElement>
                {isRebrand ? (
                  <UploadArrow color={rebrandColors.electricBlue} size="8.8" />
                ) : (
                  <Upload size="8.8" />
                )}
              </ClickableElement>
            </FileInput>
          )}
        </div>
        {layout === 'compact' && teamFolders?.length > 0 && (
          <DropDown
            className={`compact-folders-dropdown ${internalSize || ''}`}
            defaultValue="Select folder"
            menuItems={teamFolderMenuItems()}
            onChange={handleFoldersChange}
            size="small"
          />
        )}
      </div>
    </div>
  );
}

LibraryHeader.propTypes = {
  videoLibraryApi: PropTypes.instanceOf(VideoLibraryApi),
  /** Emotion base style */
  className: PropTypes.string.isRequired,
  /** Function to be called when a search is submitted */
  onSearch: PropTypes.func.isRequired,
  /** Function to be called when a video file is uploaded  */
  onUpload: PropTypes.func.isRequired,
  /** Function to be called if FileInput finds an error with one of the videos to be uploaded */
  onUploadError: PropTypes.func,
  /** Function to be called when a label is selected. */
  handleDropdownItemChange: PropTypes.func,
  /** Callback function on folder dropdown item selection */
  handleFoldersChange: PropTypes.func,
  /** Boolean loading state for folders for overlay */
  loadingTeamFolders: PropTypes.bool,
  /** Default label dropdown value. */
  defaultDropdownValue: PropTypes.string,
  /** Label dropdown values */
  dropdownItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      submenu: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        }),
      ),
    }),
  ),
  /** Title to display */
  title: PropTypes.string,
  /** Layout to render */
  layout: PropTypes.oneOf(['default', 'compact']),
  /** Handler for clients to handle their own metrics */
  onMetricTriggered: PropTypes.func,
  /** determine if insert button can be enabled */
  canInsert: PropTypes.bool,
  /** determine if insert button can be enabled */
  showInsertButton: PropTypes.bool,
  /** determine if insert button can be enabled */
  showUploadButton: PropTypes.bool,
  /** Array of team folders for folder dropdown */
  teamFolders: PropTypes.arrayOf(
    PropTypes.shape({
      createdDate: PropTypes.string,
      emailCount: PropTypes.number,
      id: PropTypes.string,
      name: PropTypes.string,
      videoCount: PropTypes.number,
    }),
  ),
  /** Size indicator for parent component */
  containerSize: PropTypes.oneOf(['', 'w360', 'chrome', 'small', 'xsmall', 'mobile']),
  /** Use new branding */
  isRebrand: PropTypes.bool,
};

LibraryHeader.defaultProps = {
  videoLibraryApi: null,
  canInsert: true,
  showInsertButton: true,
  showUploadButton: true,
  containerSize: '',
  defaultDropdownValue: 'All labels',
  dropdownItems: [],
  handleDropdownItemChange: () => {},
  handleFoldersChange: () => {},
  layout: 'default',
  loadingTeamFolders: false,
  onMetricTriggered: () => {},
  onUploadError: () => {},
  teamFolders: null,
  title: 'My Videos',
  isRebrand: false,
};

const fullSizeMargin = `${30 / 16}rem`;
const smallSizeMargin = `${20 / 16}rem`;

const styledLibraryHeader = styled(LibraryHeader)`
  position: relative;

  .library-header {
    background-color: ${colors.white};
    box-shadow: 0 0.1875rem 0.375rem ${colors.boxShadow};
    padding: 1.4375rem ${fullSizeMargin};
    z-index: 1;
    .flex-container {
      align-items: center;
      display: flex;
    }

    .title {
      ${black()}
      color: ${colors.battleship};
      font-size: 1.125rem;
      margin: 0 1.0625rem 0 0;
    }

    .searchbar-container {
      flex: 1;
      margin: 0 0.625rem 0 auto;
    }

    &--mobile,
    &--chrome,
    &--small,
    &--xsmall {
      padding: 1.4375rem ${fullSizeMargin};

      .searchbar-container {
        max-width: unset;
      }

      .file-input button {
        padding: 0 0.5rem;
      }
    }

    &--mobile,
    &--chrome,
    &--small,
    &--xsmall {
      padding: 1.4375rem ${smallSizeMargin};
    }
  }

  &.layout--compact {
    .library-header {
      box-shadow: 0px ${3 / 16}rem ${11 / 16}rem #0000000a;

      .library-header--labels {
        border: none;
        min-width: 35%;
        max-width: 50%;
        padding: 0 0.5rem 0 0;
        flex-grow: 1;

        .list {
          width: unset;
        }
      }

      .searchbar-container {
        max-width: unset;
        min-width: ${100 / 16}rem;
        margin-right: 0.5rem;
        flex-grow: 2;
        flex-basis: 50%;
      }

      .file-input svg {
        position: relative;
        top: 0.0625rem;
        width: 1.781875rem;
        height: 1.726875rem;
        path {
          transition: fill 100ms ease-in-out;
        }
      }

      .file-input:hover svg path:first-of-type {
        fill: ${colors.kermitDark};
      }
    }
  }

  .compact-folders-dropdown {
    margin-top: 10px;
    .box {
      .text {
        margin-left: 0.5rem;
      }
      svg {
        margin-right: 0.5rem;
      }
    }
    &.medium,
    &.large,
    &.xlarge {
      max-width: 35%;
    }
  }

  &.brand--new {
    .library-header {
      .title {
        ${bold()}
        color: ${colors.battleship};
        font-size: 1.125rem;
        margin: 0 1.0625rem 0 0;
      }

      .searchbar-container {
        input:placeholder-shown {
          text-overflow: ellipsis;
          padding-right: 0;
        }
      }

      .flex-container {
        height: 32px;
      }

      .button--upload,
      .button--upload .file-input {
        height: 100%;
      }

      .file-input button {
        ${mediumInter()}
        text-wrap: nowrap;

        svg path {
          transition: 0.3s;
        }

        &:hover {
          color: ${colors.white};

          svg path:first-of-type {
            fill: ${colors.white};
          }
        }
      }
    }

    &.layout--compact {
      .library-header {
        .file-input {
          background: ${rebrandColors.skyBlue};
          border-radius: 3px;
          padding: 6px;
        }

        .file-input svg {
          height: 1rem;
          path {
            transition: none;
          }
        }

        .file-input:hover {
          background: ${rebrandColors.electricBlueHover};

          & svg path:first-of-type {
            fill: ${colors.white};
          }
        }
      }
    }
  }
`;

export default styledLibraryHeader;
